import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthUser } from 'selectors';
import { useNotification } from 'hooks/useNotification';
import { useTenants } from 'TenantModule/hooks/useTenants';
import { TenantStatusTypes } from 'TenantModule/constants';
import { DecommissionPlan, ReadinessCheck, Tenant } from 'TenantModule/types';
import * as TenantServices from 'TenantModule/services';
import { TenantInfrastructureCard } from 'TenantModule/components/TenantInfrastructureCard';
import { decommissionTenant, unarchiveTenant, verifyTenant } from 'thunk';

export type TenantLifecycleModalProps = {
  onClose: VoidFunction;
  tenantId: number;
  /** Fired after a successful lifecycle transition (unarchive/decommission) so the
   * opener can drop selections pointing at rows that left the current table view. */
  onTenantChanged?: VoidFunction;
};

const statusChipColors: Record<string, 'success' | 'warning' | 'info' | 'error' | 'default'> = {
  [TenantStatusTypes.Ready]: 'success',
  // Warning, not success: mock-verified, the tenant's own infra was never checked.
  [TenantStatusTypes.MockReady]: 'warning',
  [TenantStatusTypes.Creating]: 'warning',
  [TenantStatusTypes.AwaitingInfrastructure]: 'info',
  [TenantStatusTypes.CreationFailed]: 'error',
  [TenantStatusTypes.Archived]: 'default',
  [TenantStatusTypes.PartiallyRemoved]: 'error',
  [TenantStatusTypes.Decommissioned]: 'default',
};

const checkResultColors: Record<ReadinessCheck['result'], 'success' | 'error' | 'default'> = {
  pass: 'success',
  fail: 'error',
  unknown: 'default',
};

// Verification is valid in these statuses only (server 409s otherwise).
const verifiableStatuses = new Set<TenantStatusTypes>([
  TenantStatusTypes.AwaitingInfrastructure,
  TenantStatusTypes.CreationFailed,
  TenantStatusTypes.Ready,
  // A real verify that passes upgrades Mock Ready to Ready.
  TenantStatusTypes.MockReady,
]);

// Decommission requires an archived (or retry) tenant.
const decommissionableStatuses = new Set<TenantStatusTypes>([
  TenantStatusTypes.Archived,
  TenantStatusTypes.PartiallyRemoved,
]);

const extractApiMessage = (error: any) =>
  error?.data?.message || error?.response?.data?.message || error?.message;

const ReadinessSection = ({ tenant }: { tenant: Tenant }) => {
  const report = tenant.readinessReport;

  if (!report) {
    return (
      <Typography color="text.secondary" variant="body2">
        No readiness report yet — run verification to generate one.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        Checked {report.checkedAt}
        {tenant.lastVerifiedAt ? ` · last verified ${tenant.lastVerifiedAt}` : ''}
      </Typography>
      {report.mockCheckedAgainst && (
        <Alert severity="warning" sx={{ my: 1 }}>
          Infra checks ran against <b>{report.mockCheckedAgainst}</b>
          &apos;s infrastructure (mock-check mode) — this tenant&apos;s own infra was not verified.
        </Alert>
      )}
      {report.error && (
        <Alert severity="error" sx={{ my: 1 }}>
          {report.error}
        </Alert>
      )}
      <Box component="ul" sx={{ pl: 0, my: 1, listStyle: 'none' }}>
        {(report.checks ?? []).map((check) => (
          <Box component="li" key={check.name} sx={{ mb: 0.5 }}>
            <Chip
              label={check.result}
              size="small"
              color={checkResultColors[check.result]}
              sx={{ mr: 1, minWidth: 72 }}
            />
            <Typography component="span" variant="body2">
              {check.name}
              {!check.required && ' (report-only)'}
              {check.detail ? ` — ${check.detail}` : ''}
            </Typography>
          </Box>
        ))}
      </Box>
      {!!report.deferredSteps?.length && (
        <Alert severity="info" sx={{ my: 1 }}>
          Deferred provisioning steps (re-attempted on each verification):
          <ul>
            {report.deferredSteps.map((step) => (
              <li key={step.step}>
                {step.step} — {step.reason}
              </li>
            ))}
          </ul>
        </Alert>
      )}
    </Box>
  );
};

const DecommissionPlanView = ({ plan }: { plan: DecommissionPlan }) => (
  <Box sx={{ maxHeight: 260, overflow: 'auto', my: 1 }}>
    <Alert severity="warning" sx={{ mb: 1 }}>
      {plan.ordering}
    </Alert>
    <Typography variant="subtitle2">Galaxy will remove:</Typography>
    <Box component="ul" sx={{ mt: 0.5 }}>
      <li>Auth database: {plan.galaxyOwned.authServerDatabase}</li>
      <li>DNS records: {plan.galaxyOwned.dnsRecords.length}</li>
      <li>
        Secret mappings: {plan.galaxyOwned.secretMappings.length} (infra-owned kept:{' '}
        {plan.galaxyOwned.infraOwnedMappedSecrets.length})
      </li>
      <li>Tenant-prefixed secrets: {plan.galaxyOwned.tenantPrefixedSecrets.length}</li>
      <li>JWT keys: {plan.galaxyOwned.galaxyJwtKeys.length}</li>
    </Box>
    <Typography variant="subtitle2">
      Infra residue (manual cleanup, in order after Galaxy):
    </Typography>
    <Box component="ul" sx={{ mt: 0.5 }}>
      {plan.infraResidue.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </Box>
  </Box>
);

/**
 * Tenant lifecycle panel (RDM-1409 / GLX-1827): status, readiness checklist +
 * "Run verification", unarchive, and decommission (plan view + type-the-tenant-name
 * confirmation) — the UI over the galaxy-server lifecycle state machine.
 */
export const TenantLifecycleModal = ({
  onClose,
  tenantId,
  onTenantChanged,
}: TenantLifecycleModalProps) => {
  const dispatch = useDispatch();
  const { getTenant } = useTenants();
  const tenant = getTenant({ tenantId });
  const user = useSelector(selectAuthUser);
  const { showSuccess, showError } = useNotification();

  const [isVerifying, setIsVerifying] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [isDecommissioning, setIsDecommissioning] = useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [plan, setPlan] = useState<DecommissionPlan | null>(null);
  const [confirmName, setConfirmName] = useState('');

  const status = tenant?.status;

  // The modal stays open across in-place transitions (unarchive, verify). A plan
  // loaded — and a confirmation typed — for the PREVIOUS status must not survive
  // them: a re-archived tenant would otherwise show a stale plan with the
  // type-to-confirm gate already satisfied.
  useEffect(() => {
    setPlan(null);
    setConfirmName('');
  }, [status]);

  if (!tenant || !status) return null;

  const onVerify = () => {
    setIsVerifying(true);
    // TODO: Fix with Redux Toolkit configureStore migration
    dispatch(
      verifyTenant({
        tenantName: tenant.tenantName,
        meta: {
          onSuccess: ({ response }) => {
            setIsVerifying(false);
            // Branch on the report when present; a response without one (defensive —
            // verify always persists a report) falls back to the returned status so
            // a promoted tenant is never mislabelled as not ready.
            const passed =
              response.readinessReport?.passed ??
              (response.status === TenantStatusTypes.Ready ||
                response.status === TenantStatusTypes.MockReady);
            if (passed) {
              showSuccess(`${tenant.tenantName} passed readiness verification`);
            } else {
              showError(`${tenant.tenantName} is not ready yet — see the readiness checklist`);
            }
          },
          onFailure: ({ error }) => {
            setIsVerifying(false);
            const apiMessage = extractApiMessage(error);
            const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '';
            showError(`Verification failed${apiMessageSuffix}`);
          },
        },
      }) as any,
    );
  };

  const onUnarchive = () => {
    setIsUnarchiving(true);
    // TODO: Fix with Redux Toolkit configureStore migration
    dispatch(
      unarchiveTenant({
        tenantName: tenant.tenantName,
        meta: {
          onSuccess: () => {
            setIsUnarchiving(false);
            showSuccess(`${tenant.tenantName} unarchived — it re-earns Ready via verification`);
            onTenantChanged?.();
          },
          onFailure: ({ error }) => {
            setIsUnarchiving(false);
            const apiMessage = extractApiMessage(error);
            const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '';
            showError(`Unarchive failed${apiMessageSuffix}`);
          },
        },
      }) as any,
    );
  };

  const onLoadPlan = async () => {
    setIsLoadingPlan(true);
    try {
      const response = await TenantServices.fetchDecommissionPlan(
        tenant.tenantName,
        {},
        user?.auth_token ?? '',
      );
      setPlan(response.data);
    } catch (error) {
      const apiMessage = extractApiMessage(error);
      const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '';
      showError(`Could not load the decommission plan${apiMessageSuffix}`);
    } finally {
      setIsLoadingPlan(false);
    }
  };

  const onDecommission = () => {
    setIsDecommissioning(true);
    // TODO: Fix with Redux Toolkit configureStore migration
    dispatch(
      decommissionTenant({
        tenantName: tenant.tenantName,
        confirmTenantName: confirmName,
        meta: {
          onSuccess: () => {
            setIsDecommissioning(false);
            showSuccess(`${tenant.tenantName} decommissioned`);
            onTenantChanged?.();
            onClose();
          },
          onFailure: ({ error }) => {
            setIsDecommissioning(false);
            const apiMessage = extractApiMessage(error);
            const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '';
            // Partial failures land in Partially Removed — the same button retries.
            showError(`Decommission failed${apiMessageSuffix}`);
          },
        },
      }) as any,
    );
  };

  return (
    <Dialog maxWidth="sm" fullWidth open onClose={onClose}>
      <DialogTitle>
        Lifecycle: {tenant.tenantName}{' '}
        <Chip
          label={status}
          size="small"
          color={statusChipColors[status] || 'default'}
          sx={{ ml: 1 }}
        />
      </DialogTitle>
      <DialogContent>
        <Box component="h4" sx={{ mb: 1 }}>
          Readiness
        </Box>
        <ReadinessSection tenant={tenant} />
        <Button
          variant="outlined"
          onClick={onVerify}
          disabled={!verifiableStatuses.has(status) || isVerifying}
          sx={{ mt: 1 }}
        >
          {isVerifying ? 'Verifying...' : 'Run verification'}
        </Button>
        {!verifiableStatuses.has(status) && (
          <Typography variant="caption" display="block" color="text.secondary">
            Verification applies to Awaiting Infrastructure / Creation Failed / Ready tenants.
          </Typography>
        )}

        <TenantInfrastructureCard tenant={tenant} />

        {status === TenantStatusTypes.Archived && (
          <>
            <Box component="h4" sx={{ mt: 3, mb: 1 }}>
              Unarchive
            </Box>
            <Typography variant="body2" color="text.secondary">
              Restores the tenant to Awaiting Infrastructure; the server then re-runs verification
              automatically. Archived tenants keep all their data — only decommission removes
              infrastructure.
            </Typography>
            <Button
              variant="outlined"
              onClick={onUnarchive}
              disabled={isUnarchiving || isDecommissioning}
              sx={{ mt: 1 }}
            >
              {isUnarchiving ? 'Unarchiving...' : 'Unarchive'}
            </Button>
          </>
        )}

        {decommissionableStatuses.has(status) && (
          <>
            <Box component="h4" sx={{ mt: 3, mb: 1 }}>
              Decommission
            </Box>
            <Alert severity="error" sx={{ mb: 1 }}>
              Permanently removes the tenant&apos;s Galaxy-owned resources (auth database, secrets,
              DNS). Deleted secrets are recoverable for 30 days; the database survives only as its
              final backup.
            </Alert>
            {plan ? (
              <DecommissionPlanView plan={plan} />
            ) : (
              <Button variant="outlined" onClick={onLoadPlan} disabled={isLoadingPlan}>
                {isLoadingPlan ? 'Loading plan...' : 'Load decommission plan'}
              </Button>
            )}
            {plan && (
              <Box sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={`Type "${tenant.tenantName}" to confirm`}
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  disabled={isDecommissioning}
                />
                <Button
                  variant="contained"
                  color="error"
                  onClick={onDecommission}
                  disabled={
                    confirmName !== tenant.tenantName ||
                    isDecommissioning ||
                    // An in-flight unarchive is about to make this tenant
                    // non-decommissionable — a second click racing it could remove
                    // resources after the restore.
                    isUnarchiving
                  }
                  sx={{ mt: 1 }}
                >
                  {isDecommissioning
                    ? 'Decommissioning...'
                    : status === TenantStatusTypes.PartiallyRemoved
                      ? 'Retry decommission'
                      : 'Decommission tenant'}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
